// form for editing scorecard node weights

import Backbone from 'backbone'

import template from './weight-form.hbs'
import _ from 'lodash'
import $ from 'jquery'
import MarkedField from 'scoreboard/components/marked-field'
import numericEvaluator from 'scoreboard/components/evaluators/numeric-evaluator'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import nonNegativeNumberValidator from 'scoreboard/components/validators/non-negative-number-validator'
import FormView from 'scoreboard/components/form-view'
import formatValue from 'scoreboard/components/format-value'
import visible from '../visible'
import globals from 'scoreboard/components/globals'
import calendars from 'scoreboard/components/calendars'
import appDisplay from 'scoreboard/components/app-display'
import WeightDateRanges from './weight-date-ranges-form'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import conductor from 'scoreboard/components/conductor'
import i18n from 'scoreboard/components/i18n'
import * as urlToLinks from '../../components/urls-to-links'

function markField(el) {
	return new MarkedField({
		el: el,
		evaluator: numericEvaluator,
		validator: [requiredValidator, nonNegativeNumberValidator]
	}).render()
}

/**
 * Calculates totals and percents based on the user's input. The input is an
 * array of weights.
 *
 * The returned data is of the format
 *
 * <pre>
 * {
 * 	weights: [ .25, .25, .5 ],
 * 	total: 4
 * }
 * </pre>
 */
function calculate(weights) {
	// convert weights to numbers
	var weights = _.map(weights, function(weight) {
		var number = Number(weight)
		return isNaN(number) ? 0 : number
	})

	// do the calculations
	var total = _.sum(weights)
	var percents = _.map(weights, function(weight) {
		return total == 0 ? 0 : weight / total
	})

	return {
		percents: percents,
		total: formatValue({value: total, shorten: false})
	}
}

var WeightForm = Backbone.View.extend({

	// this.model is the ScorecardNode the user clicked on to bring up this form
	// this.weights are the weights for the siblings of the given ScorecardNode

	className: 'weight-form',

	tagName: 'form',

	events: {
		'keyup input': 'update',
		'click .edit-dates' : '_editDates'
	},

	initialize: function(options = {}) {
		this.originalWeights = options.weights
		this.weights = _.cloneDeep(this.originalWeights)
		this.weightsById = _.keyBy(this.weights, 'id')

		this.formView = new FormView({ el: this.el, submit: this.submit.bind(this) })
	},

	render: function() {
		// render template
		this.$el.empty().append(template(this.context()))

		// add behavior to the inputs
		this._markedFields = []
		_.each(this.$('input'), function(input) {
			this._markedFields.push(markField(input))
		}.bind(this))

		this.update()

		if (!this.formView.started) {
			this.formView.start()
		}

		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		return this
	},

	context() {
		let period = calendars.periodsById[globals.periodId]
		//#29449 hide period if none have varying weights
		let hasVaryingWeights = false
		// #20226 Omit archived objects from Edit Weighting screen
		let weights = _.filter(this.weights, weight => visible(weight, period))
		let currentWeightId = conductor.id()
		_.each(weights, weightNode => {
			weightNode.showPeriods = weightNode.periods.length > 1
			weightNode.currentWeight = WeightDateRanges.weightInDateRange(weightNode.periods, period)
			hasVaryingWeights = hasVaryingWeights || weightNode.showPeriods
			weightNode.isSelected = currentWeightId == weightNode.id
		})
		// #31170 Temporarily remove all support URLs - original mr.p key has
		// been commented out; current 'scorecards.node.currentWeights.info' key
		// will not display link
		let supportArticleLink = this.constructSupportUrl()

		return { weights, period, hasVaryingWeights, supportArticleLink  }
	},

	constructSupportUrl: function() {
		let linkText = i18n.get('scorecards.node.currentWeights.info.support.link.text')
		return urlToLinks.createAnchor('https://support.spiderstrategies.com/hc/en-us/articles/360041553391-Scoring', linkText)
	},

	// update the display to show the correct totals and percent weights
	update: function(evt) {
		var formatted

		//update the all time weight
		if (evt) {
			let defaultWeightInput = this.$(evt.target)
			this.weightsById[defaultWeightInput.data('id')].periods[0].weight =
				defaultWeightInput.val()
		}

		// do the calculations
		var weights = _.map(this.$('input'), el => {
			return el.value
		})
		var calculations = calculate(weights)

		var percents = this.$('.percent')
		_.forEach(calculations.percents, function(value, index) {
			var formatted = formatValue({ value: value, dataType: 'percent' })
			percents.eq(index).text(formatted)
		})

		formatted = formatValue({ value: 1, dataType: 'percent' })
		this.$('.total .percent').text(formatted)

		this.$('.total .weight').text(calculations.total)
	},

	_editDates: function(e) {
		let id = $(e.target).data('id')
		let weightNode = this.weightsById[id]

		let weightDateForm = new WeightDateRanges({weightNode})
		appDisplay.dialog(weightDateForm)
		weightDateForm.on('done', weights => {
			weightNode.periods = weights
			this.formView.setDirty()
			this.render()
		})
	},

	submit: function(e) {
		e.preventDefault()

		// #20469 take visibility into account
		let filtered = this.context().weights

		//only send changed weights
		let originalWeightsById = _.keyBy(this.originalWeights, 'id')
		let changed = weightNode => {
			let originalWeightNode = originalWeightsById[weightNode.id]
			if (originalWeightNode.periods.length != weightNode.periods.length) {
				return true
			}
			return _.some(weightNode.periods, (weight, index) => {
				let originalWeight = originalWeightNode.periods[index]
				let newWeight = weight
				return originalWeight.start != newWeight.start ||
					originalWeight.end != newWeight.end ||
					originalWeight.weight != newWeight.weight
			})
		}

		// #22284 take editability into account
		filtered = _.filter(filtered, weightNode => {
			return weightNode.editable && changed(weightNode)
		})

		if (filtered.length) {
			this.model.saveWeights(filtered)
		}
		//#29570 send the state of all weights so calculation is correct
		this.trigger('save', this.context().weights)
		this.trigger('done')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this._markedFields, this.formView,
			this.covertButtonTooltips], 'remove')
	}

})

_.extend(WeightForm, {
	calculate
})
export default WeightForm
export { calculate }

WeightForm.__filename__ = 'scorecards/edit/weight-form.js'
